import React from "react";
import { UserNotificationWithDetails } from "../../types/user_notifications.types";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import {
  Ship,
  CreditCard,
  Users,
  Anchor,
  X,
  Trophy,
  CheckCircle2,
} from "lucide-react";
import { useUserNotifications } from "../../context/UserNotificationsContext";

interface NotificationsDropdownProps {
  onClose: () => void;
  isMobile?: boolean;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  ship: Ship,
  "credit-card": CreditCard,
  users: Users,
  anchor: Anchor,
  trophy: Trophy,
  "check-circle": CheckCircle2,
};

const replaceCustomData = (
  text: string,
  customData: Record<string, any> | null
): string => {
  if (!customData) return text;

  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return customData[key]?.toString() || match;
  });
};

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  onClose,
  isMobile = false,
}) => {
  const { notifications, isLoading, markAsRead, deleteNotification } =
    useUserNotifications();

  const handleNotificationClick = async (
    notification: UserNotificationWithDetails
  ) => {
    if (notification.status === "unread") {
      await markAsRead(notification.id);
    }
  };

  const handleDeleteClick = async (
    e: React.MouseEvent,
    notificationId: number
  ) => {
    e.stopPropagation();
    await deleteNotification(notificationId);
  };

  if (isLoading) {
    return null;
  }

  if (!notifications || notifications.length === 0) {
    return (
      <div
        className={`w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4 ${
          isMobile ? "fixed right-4 top-16 z-50" : ""
        }`}
      >
        <div className="text-center text-gray-500">No hay notificaciones</div>
      </div>
    );
  }

  return (
    <div
      className={`w-80 bg-white rounded-lg shadow-lg border border-gray-200 ${
        isMobile ? "fixed right-4 top-16 z-50" : ""
      }`}
    >
      <div className="p-2 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">Notificaciones</h3>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {notifications.map((notification) => {
          const IconComponent = notification.icon
            ? iconMap[notification.icon]
            : null;

          const title = replaceCustomData(
            notification.title,
            notification.custom_data
          );
          const description = replaceCustomData(
            notification.description,
            notification.custom_data
          );

          return (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`p-4 hover:bg-gray-50 cursor-pointer relative group ${
                notification.status === "unread" ? "bg-blue-50" : ""
              }`}
            >
              <button
                onClick={(e) => handleDeleteClick(e, notification.id)}
                className="absolute right-2 top-2 p-1 rounded-full hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>

              <div className="flex items-start">
                {IconComponent && (
                  <div className="mr-3 mt-1">
                    <IconComponent className="h-5 w-5 text-gray-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 truncate">
                    {title}
                  </h4>
                  <p className="mt-1 text-xs text-gray-500 italic">
                    {formatDistanceToNow(new Date(notification.updated_at), {
                      addSuffix: true,
                      locale: es,
                    })}
                  </p>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-4">
                    {description}
                  </p>
                  {notification.action_url && (
                    <a
                      href={notification.action_url}
                      className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNotificationClick(notification);
                      }}
                    >
                      Ver más
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
